// file: src/context/ViewerContext.jsx
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getJsonLocalForage, setJsonLocalForage } from '../utils/localForageUtils';
import { useProjectContext } from './ProjectContext';
import { safeIndexedDBWrite, onVisibilityChange } from '../utils/mobileOptimizations';

const ViewerContext = createContext(null);

export function ViewerProvider({ children }) {
const [openFiles, setOpenFiles] = useState([]);
const [activeFileId, setActiveFileId] = useState(null);
const [jump, setJump] = useState(null); // {id, page, timestamp}
const [isInitialized, setIsInitialized] = useState(false);
const [isPageVisible, setIsPageVisible] = useState(true);

// Get projectId from URL
const projectId = new URLSearchParams(window.location.search).get('project');

  // Use the project context hook at the top level (React rules of hooks)
  const { projects } = useProjectContext();

  // Helper to get the latest file object from projects by id
  const getLatestFileById = (fileId) => {
    for (const project of projects || []) {
      for (const phase of project.phases || []) {
        const found = (phase.files || []).find(f => f.id === fileId);
        if (found) return found;
      }
    }
    return null;
  };

// Load cached state on mount and when project changes
// NOTE: Intentionally NOT including 'projects' in deps to prevent re-running
// this effect on every polling update (which would cause viewer re-renders)
useEffect(() => {
async function loadCachedState() {
if (!projectId) return;
// Add a small delay to ensure projects context is ready
await new Promise(resolve => setTimeout(resolve, 100));
const savedOpenFiles = await getJsonLocalForage(`openFiles-${projectId}`);
const savedActiveId = await getJsonLocalForage(`activeFileId-${projectId}`);
      if (savedOpenFiles) {
        // Merge in latest file data from projects
        const mergedFiles = savedOpenFiles.map(f => {
          const latest = getLatestFileById(f.id);
          return latest ? { ...latest, ...f } : f;
        });
        setOpenFiles(mergedFiles);
      }
if (savedActiveId) setActiveFileId(savedActiveId);
setIsInitialized(true);
}
loadCachedState();
  }, [projectId]);

// Listen for visibility changes to pause caching when tab is hidden
useEffect(() => {
  const cleanup = onVisibilityChange((isHidden) => {
    setIsPageVisible(!isHidden);
  });
  return cleanup;
}, []);

// Persist state changes with aggressive debouncing - only after initialization
// Only cache when page is visible to prevent wasted operations
useEffect(() => {
if (!projectId || !isInitialized || !isPageVisible) return;

const timeoutId = setTimeout(() => {
  // Use safe IndexedDB write that won't block on mobile
  safeIndexedDBWrite(() => setJsonLocalForage(`openFiles-${projectId}`, openFiles))
    .catch(err => {
      console.error('[ViewerContext] Failed to cache openFiles:', err);
    });
}, 1500); // Increased from 250ms to 1500ms for better mobile performance

return () => clearTimeout(timeoutId);
}, [openFiles, projectId, isInitialized, isPageVisible]);

useEffect(() => {
if (!projectId || !isInitialized || !isPageVisible) return;

const timeoutId = setTimeout(() => {
  // Use safe IndexedDB write that won't block on mobile
  safeIndexedDBWrite(() => setJsonLocalForage(`activeFileId-${projectId}`, activeFileId))
    .catch(err => {
      console.error('[ViewerContext] Failed to cache activeFileId:', err);
    });
}, 1500); // Increased from 250ms to 1500ms for better mobile performance

return () => clearTimeout(timeoutId);
}, [activeFileId, projectId, isInitialized, isPageVisible]);

  // Utility to open a file at a specific page for citations
  const openFileAtPage = useCallback(({ file, page }) => {
    setOpenFiles(curr => {
      const exists = curr.some(f => f.id === file.id);
      if (exists) return curr;
      return [...curr, file];
    });
    setActiveFileId(file.id);
    
    // Validate and normalize page number
    const pageNum = Math.max(1, parseInt(page, 10) || 1);
    
    // Always update jump, even if file is already open
    setJump({
      id: file.id,
      page: pageNum,
      timestamp: Date.now(),
      instant: true  
    });
  }, []);

// Tab management functions
const closeFile = useCallback((fileIdOrFile) => {
setOpenFiles(prev => {
    let filtered;
    // Support both id and file object
    let fileId = typeof fileIdOrFile === 'string' ? fileIdOrFile : fileIdOrFile?.id;
    let fileType = fileIdOrFile?.type;
    let serverName = fileIdOrFile?.server_name || (fileIdOrFile?.location ? fileIdOrFile.location.split('/').pop() : undefined);
    if (fileType === 'jupyterlab' && serverName) {
      // Remove both the DB id and the synthetic JupyterLab tab id
      filtered = prev.filter(f => f.id !== fileId && f.id !== `jupyterlab-${serverName}`);
    } else {
      filtered = prev.filter(f => f.id !== fileId);
    }
// If closing the active file, switch to another
    if ((fileId === activeFileId || (fileType === 'jupyterlab' && activeFileId === `jupyterlab-${serverName}`)) && filtered.length > 0) {
setActiveFileId(filtered[0].id);
} else if (filtered.length === 0) {
setActiveFileId(null);
}
    // Remove from localForage cache as well
    if (projectId) {
      setJsonLocalForage(`openFiles-${projectId}`, filtered);
    }
return filtered;
});
}, [activeFileId, projectId]);

const openFile = useCallback((file) => {
const alreadyOpen = openFiles.some(f => f.id === file.id);
if (!alreadyOpen) {
setOpenFiles(prev => [...prev, file]);
}
setActiveFileId(file.id);
}, [openFiles]);

const value = {
openFiles,
setOpenFiles,
activeFileId,
setActiveFileId,
jump,
setJump,
openFileAtPage,
closeFile,
openFile,
isInitialized
};

return (
<ViewerContext.Provider value={value}>
{children}
</ViewerContext.Provider>
);
}

export function useViewerContext() {
const context = useContext(ViewerContext);
if (!context) {
throw new Error('useViewerContext must be used within a ViewerProvider');
}
return context;
}